import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { hexToRgba, normalizeHex } from "@/shared/utils/statusChipColors";

export interface StatusChipProps {
  text: string;
  color: string;
  count?: number;
  size?: "sm" | "md";
  variant?: "outline" | "soft";
  className?: string;
  onRemove?: () => void;
}

export function StatusChip({
  text,
  color,
  count,
  size = "sm",
  variant = "soft",
  className,
  onRemove,
}: StatusChipProps) {
  const normalized = normalizeHex(color);
  const bgRgba = hexToRgba(normalized, 0.12);
  const displayText = count != null ? `${text} ${count}` : text;

  const style: React.CSSProperties =
    variant === "soft"
      ? {
          backgroundColor: bgRgba,
          borderColor: normalized,
          color: normalized,
        }
      : {
          backgroundColor: "transparent",
          borderColor: normalized,
          color: normalized,
        };

  return (
    <span
      role="status"
      title={displayText}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border font-medium",
        size === "sm" && "px-2 py-0.5 text-xs",
        size === "md" && "px-2.5 py-1 text-sm",
        onRemove && "pr-1",
        className
      )}
      style={style}
    >
      {displayText}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="rounded-full p-0.5 hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-offset-1"
          aria-label={`Remove ${text}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}
